import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';
import baseURL from './baseURl';
import logo from './logo-black.png';
import './Transactions.css';
import { Trash, PencilFill, PrinterFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

const Transactions = () => {
  // State Management
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Initialize with an empty array
  const [searchParams, setSearchParams] = useState({
    receiptNo: '',
    clientName: '',
    startDate: '',
    endDate: '',
    transactionType: ''
  });

  // Modal and Edit States
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Loading and Error States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Transaction Types
  const TRANSACTION_TYPES = [
    'buy', 
    'sell', 
    'advance', 
    'cash', 
    'pure_weight'
  ];

  // Utility Functions
  const formatPureWeight = (weight) => {
    if (!weight) return '0.000';
    const [whole, decimal = ''] = String(weight).split('.');
    const formattedDecimal = (decimal + '000').slice(0, 3);
    return `${whole}.${formattedDecimal}`;
  };

  const calculatePureWeight = (weights, touches) => {
    if (!Array.isArray(weights) || !Array.isArray(touches)) return 0;
    
    return weights.reduce((total, weight, index) => {
      const touch = touches[index] || 0;
      return total + ((parseFloat(weight) || 0) * (parseFloat(touch) || 0)) / 100;
    }, 0).toFixed(3);
  };

  const calculateAmount = (weight, touch, rate) => {
    weight = parseFloat(weight) || 0;
    touch = parseFloat(touch) || 0;
    rate = parseFloat(rate) || 0;
    return Math.round((weight * touch * rate) / 100);
  };

  const calculateTransactionAmounts = (transaction) => {
    let totalAmount = 0;
    const totalAmounts = []; // Add this to store individual row amounts
    
    if (transaction.articleType === 'langdiWeight') {
      // For langdi weight: (weight * touch / 100) * (rate / 10)
      transaction.rate.forEach((rate, index) => {
        const weight = parseFloat(transaction.langdiWeight[index] || 0);
        const touch = parseFloat(transaction.touch[index] || 0);
        const currentAmount = weight * (touch / 100) * (parseFloat(rate) / 10);
        totalAmounts.push(currentAmount);
        totalAmount += currentAmount;
      });
    } else if (transaction.articleType === 'pureWeight') {
      // For pure weight: amount * (rate / 10)
      transaction.rate.forEach((rate, index) => {
        const currentAmount = parseFloat(transaction.amount[index] || 0) * (parseFloat(rate) / 10);
        totalAmounts.push(currentAmount);
        totalAmount += currentAmount;
      });
    }

    // Calculate pending amount
    const amountGiven = parseFloat(transaction.amountGiven || 0);
    const pendingAmount = totalAmount - amountGiven;

    return {
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      pendingAmount: parseFloat(pendingAmount.toFixed(2)),
      totalAmounts: totalAmounts // Return the array of individual amounts
    };
  };

  const updateTransactionRow = (transaction, index, field, value) => {
    const updatedTransaction = { ...transaction };
    
    // Update the specific field
    switch (field) {
      case 'weight':
        updatedTransaction.langdiWeight = [...(updatedTransaction.langdiWeight || [])];
        updatedTransaction.langdiWeight[index] = parseFloat(value);
        break;
      case 'touch':
        updatedTransaction.touch = [...(updatedTransaction.touch || [])];
        updatedTransaction.touch[index] = parseFloat(value);
        break;
      case 'rate':
        updatedTransaction.rate = [...(updatedTransaction.rate || [])];
        updatedTransaction.rate[index] = parseFloat(value);
        break;
      case 'amount':
        updatedTransaction.amount = [...(updatedTransaction.amount || [])];
        updatedTransaction.amount[index] = parseFloat(value);
        break;
    }

    // Recalculate amounts
    const { totalAmount, pendingAmount, totalAmounts } = calculateTransactionAmounts(updatedTransaction);
    updatedTransaction.totalAmount = totalAmount;
    updatedTransaction.pendingAmount = pendingAmount;
    updatedTransaction.totalAmounts = totalAmounts;
    updatedTransaction.total = totalAmount;

    return updatedTransaction;
  };

  // Fetch and Filter Functions
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching Transactions - Search Parameters:', {
        searchParams: searchParams,
        timestamp: new Date().toISOString()
      });

      const response = await axios.get(`${baseURL}/api/transactions`, {
        params: searchParams
      });
      
      console.log('📦 Transactions Fetch Response:', {
        status: response.status,
        totalTransactions: response.data.length,
        firstTransaction: response.data[0],
        lastTransaction: response.data[response.data.length - 1]
      });

      setTransactions(response.data);
      
      // Initial filtering
      const filtered = filterTransactions(response.data, searchParams);
      
      console.log('🔬 Filtered Transactions:', {
        totalFiltered: filtered.length,
        filterParams: searchParams,
        filterDetails: filtered.map(t => ({
          receiptNo: t.receiptNo,
          type: t.type,
          clientName: t.clientName
        }))
      });
      
      setFilteredTransactions(filtered);
    } catch (error) {
      console.error('❌ Error in Fetching Transactions:', {
        message: error.message,
        errorResponse: error.response?.data,
        status: error.response?.status,
        url: `${baseURL}/api/transactions`,
        searchParams: searchParams
      });
      
      setError(`Error in fetching transactions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = (data, params) => {
    if (!data || !Array.isArray(data)) return [];

    let filtered = data;

    // Filter by Receipt No
    if (params.receiptNo) {
      filtered = filtered.filter(t => 
        t.receiptNo.toLowerCase().includes(params.receiptNo.toLowerCase())
      );
    }

    // Filter by Client Name
    if (params.clientName) {
      filtered = filtered.filter(t => 
        t.clientName.toLowerCase().includes(params.clientName.toLowerCase())
      );
    }

    // Filter by Transaction Type
    if (params.transactionType) {
      filtered = filtered.filter(t => 
        t.type === params.transactionType || 
        t.advanceType === params.transactionType
      );
    }

    // Filter by Date Range
    if (params.startDate && params.endDate) {
      const start = new Date(params.startDate);
      const end = new Date(params.endDate);
      
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    return filtered;
  };

  // Transaction Management Functions
  const handleEditTransaction = (transaction) => {
    const normalizedTransaction = normalizeTransactionForEdit(transaction);
    setSelectedTransaction(normalizedTransaction);
    setShowEditModal(true);
  };

  const normalizeTransactionForEdit = (transaction) => {
    const maxLength = Math.max(
      transaction.langdiWeight?.length || 1,
      transaction.touch?.length || 1,
      transaction.rate?.length || 1,
      transaction.amount?.length || 1
    );

    const normalizeArray = (arr, defaultValue = 0) => {
      if (!Array.isArray(arr)) arr = [arr];
      while (arr.length < maxLength) {
        arr.push(defaultValue);
      }
      return arr;
    };

    return {
      ...transaction,
      langdiWeight: normalizeArray(transaction.langdiWeight),
      touch: normalizeArray(transaction.touch, 0), 
      rate: normalizeArray(transaction.rate),
      amount: normalizeArray(transaction.amount),
      totalAmounts: normalizeArray(transaction.totalAmounts),
      pureWeightGiven: transaction.pureWeightGiven || 0
    };
  };

  const saveTransactionEdit = async () => {
    try {
      const transactionToSave = { ...selectedTransaction };

      const { totalAmount, pendingAmount, totalAmounts } = calculateTransactionAmounts(transactionToSave);
      transactionToSave.totalAmount = totalAmount;
      transactionToSave.pendingAmount = pendingAmount;
      transactionToSave.totalAmounts = totalAmounts;

      const response = await axios.put(`${baseURL}/api/transactions/update`, transactionToSave);
      
      const updatedTransactions = transactions.map(t => 
        t._id === response.data._id ? response.data : t
      );
      
      setTransactions(updatedTransactions);
      setShowEditModal(false);
      setSelectedTransaction(null);
    } catch (error) {
      setError(`Error in updating transaction: ${error.message}`);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this transaction?'
      );
      
      if (!confirmDelete) return;

      await axios.delete(`${baseURL}/api/transactions/transactions/${transactionId}`);

      setTransactions(prevTransactions => 
        prevTransactions.filter(t => t._id !== transactionId)
      );

      setFilteredTransactions(prevTransactions => 
        prevTransactions.filter(t => t._id !== transactionId)
      );
    } catch (error) {
      setError(`Error in deleting transaction: ${error.message}`);
    }
  };

  const handlePrintTransaction = (transaction) => {
    if (!transaction) {
      console.error('No transaction data available for printing.');
      return;
    }

    const { date, receiptNo, clientName, langdiWeight, touch, rate, amountToBePaid, amountToBeAdded, amountGiven } = transaction;
    if (!date || !receiptNo || !clientName) {
      console.error('Transaction is missing required properties.');
      return;
    }

    const printWindow = document.createElement('iframe');
    printWindow.style.position = 'absolute';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = 'none';
    document.body.appendChild(printWindow);

    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Print Transaction</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background-color: #f9f9f9; }');
    doc.write('.container { width: 80%; margin: 0 auto; padding: 20px; background-color: #fff; border: 1px solid #ccc; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); }');
    doc.write('h1 { text-align: center; margin: 10px 0; font-size: 20px; }');
    doc.write('.header { text-align: left; margin-bottom: 20px; }');
    doc.write('.logo { display: inline-block; width: 100px; float: left; }');
    doc.write('.date { display: inline-block; float: right; font-size: 14px; }');
    doc.write('.clear { clear: both; }');
    doc.write('table { width: 100%; margin-top: 20px; border-collapse: collapse; }');
    doc.write('th, td { border: 1px solid #000; padding: 8px; text-align: center; }');
    doc.write('th { background-color: #f2f2f2; }');
    doc.write('.footer { margin-top: 20px; border-top: 1px solid #000; padding-top: 10px; text-align: center; }');
    doc.write('</style>');
    doc.write('</head><body>');
    doc.write('<div class="container">');
    doc.write('<h1>SHREE GANESHAY NAMH</h1>');
    doc.write('<div class="header">');
    doc.write('<p class="date">' + new Date(date).toLocaleDateString('en-GB') + '</p>');
    doc.write('<img src="' + logo + '" class="logo" alt="Logo" style="height: 50px; width: 50px;">');
    doc.write('</div>');
    doc.write('<div class="clear"></div>');
    doc.write('<p><strong>Receipt No:</strong> ' + (receiptNo || 'N/A') + '</p>');
    doc.write('<p><strong>Name:</strong> ' + (clientName || 'N/A') + '</p>');
    doc.write('<table>');
    doc.write('<thead><tr><th>Langdi Weight</th><th>Touch</th><th>Pure Weight</th><th>Pure Weight Given</th><th>Balance Weight</th><th>Rate</th><th>Total</th></tr></thead>');
    doc.write('<tbody>');
    (langdiWeight || []).forEach((weight, index) => {
      doc.write('<tr>');
      doc.write('<td>' + weight + '</td>');
      doc.write('<td>' + (touch?.[index] || '0') + '</td>');
      // For the first row, write the merged cells
      if (index === 0) {
        doc.write('<td rowspan="' + (langdiWeight || []).length + '">' + (transaction.pureWeightCalculated || '0.000') + '</td>');
        doc.write('<td rowspan="' + (langdiWeight || []).length + '">' + (transaction.pureWeightGiven || '0.000') + '</td>');
        doc.write('<td rowspan="' + (langdiWeight || []).length + '">' + (transaction.balanceWeight ? transaction.balanceWeight.toFixed(3) : '0.000') + '</td>');
        doc.write('<td rowspan="' + (langdiWeight || []).length + '">' + (Array.isArray(transaction.rate) ? transaction.rate[0] : '0.000') + '</td>');
        doc.write('<td rowspan="' + (langdiWeight || []).length + '">₹' + ((amountToBePaid || 0) + (amountToBeAdded || 0) + (amountGiven || 0)) + '</td>');
      }
      doc.write('</tr>');
    });
    doc.write('</tbody>');
    doc.write('</table>');
    doc.write('<div class="footer">');
    doc.write('<p>Thank you for your Business!</p>');
    doc.write('<p>Do visit Again!</p>');
    doc.write('</div>');
    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();

    // Wait for the iframe content to load before printing
    printWindow.onload = function() {
        printWindow.contentWindow.focus();
        printWindow.contentWindow.print();
        document.body.removeChild(printWindow); // Clean up the iframe
    };
  };

  const handleBuyReceipt = async (transaction) => {
    try {
      // Navigate to Buy component with transaction details
      navigate('/buy', { 
        state: { 
          transaction: transaction,
          fromTransactions: true 
        } 
      });
    } catch (error) {
      console.error('Error handling Buy receipt:', error);
      setErrorMessage('Failed to load Buy receipt');
    }
  };

  const handleSpeedReceipt = async (transaction) => {
    try {
      // Navigate to Speed component with transaction details
      navigate('/speed', { 
        state: { 
          transaction: transaction,
          fromTransactions: true 
        } 
      });
    } catch (error) {
      console.error('Error handling Speed receipt:', error);
      setErrorMessage('Failed to load Speed receipt');
    }
  };

  // Modal and UI Helpers
  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedTransaction(null);
  };

  const handleError = (error, context) => {
    const errorMessage = error.response?.data?.message || error.message;
    setError(`Error in ${context}: ${errorMessage}`);
  };

  // Effect Hooks
  const navigate = useNavigate();
  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    const filtered = filterTransactions(transactions, searchParams);
    setFilteredTransactions(filtered);
  }, [searchParams, transactions]);

  // Render Methods
  const renderTransactionTable = () => (
    <div className="transaction-table">
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Receipt No</th>
            <th>Client Name</th>
            <th>Type</th>
            <th>Date</th>
            <th>Weight</th>
            {/* Conditionally add Langdi Weight column */}
            {filteredTransactions.some(t => t.type === 'langdiWeight') && <th>Langdi Weight</th>}
            {/* Conditionally add Langdi Weight Amount column */}
            {filteredTransactions.some(t => t.articleType === 'langdiWeight') && <th>Langdi Weight Amount</th>}
            {/* Conditionally add Pure Weight column */}
            {filteredTransactions.some(t => t.articleType === 'pureWeight') && <th>Pure Weight Amount</th>}
            {/* Conditionally add Cash Amount column */}
            {filteredTransactions.some(t => t.articleType === 'cashAmount') && <th>Cash Amount</th>}
            <th>Total Amount</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map(transaction => (
            <tr key={transaction._id}>
              <td>{transaction.receiptNo}</td>
              <td>{transaction.clientName || transaction.note}</td>
              <td>{transaction.type || transaction.advanceType}</td>
              <td>{new Date(transaction.date).toLocaleDateString()}</td>
              <td>{formatPureWeight(transaction.pureWeightCalculated)}</td>
              {/* Conditionally render Langdi Weight column */}
              {filteredTransactions.some(t => t.type === 'langdiWeight') && (
                <td>
                  {transaction.type === 'langdiWeight' 
                    ? transaction.langdiWeight?.reduce((sum, weight) => sum + (parseFloat(weight) || 0), 0).toFixed(3) 
                    : '-'}
                </td>
              )}
              {/* Conditionally render Langdi Weight Amount column */}
              {filteredTransactions.some(t => t.articleType === 'langdiWeight') && (
                <td>
                  {transaction.articleType === 'langdiWeight' 
                    ? (transaction.amount?.[0] || 0).toLocaleString()
                    : '-'}
                </td>
              )}
              {/* Conditionally render Pure Weight Amount column */}
              {filteredTransactions.some(t => t.articleType === 'pureWeight') && (
                <td>
                  {(transaction.articleType === 'pureWeight' && 
                    (transaction.transactionType === 'advance' && 
                     ['given', 'taken'].includes(transaction.advanceType))) 
                    ? (transaction.total || 0).toLocaleString()
                    : (transaction.articleType === 'pureWeight' 
                      ? (transaction.amount?.[0] || 0).toLocaleString()
                      : '-')}
                </td>
              )}
              {/* Conditionally render Cash Amount column */}
              {filteredTransactions.some(t => t.articleType === 'cashAmount') && (
                <td>
                  {transaction.articleType === 'cashAmount' 
                    ? (transaction.amount?.[0] || 0).toLocaleString()
                    : '-'}
                </td>
              )}
              <td>
                {(transaction.transactionType === 'advance' || 
                  transaction.type === 'adding' || 
                  transaction.type === 'selling') 
                  ? '-' 
                  : `₹${(transaction.total || 0).toLocaleString()}`}
              </td>
              <td>
                <span className={`badge bg-${getStatusColor(transaction.status)}`}>
                  {transaction.status}
                </span>
              </td>
              <td>
                <div className="d-flex">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-1"
                    onClick={() => handleEditTransaction(transaction)}
                  >
                    <PencilFill />
                  </Button>
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="me-1"
                    onClick={() => handlePrintTransaction(transaction)}
                  >
                    <PrinterFill /> Print Receipt
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDeleteTransaction(transaction._id)}
                  >
                    <Trash />
                  </Button>
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={() => handleBuyReceipt(transaction)}
                  >
                    Buy Receipt
                  </Button>
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={() => handleSpeedReceipt(transaction)}
                  >
                    Speed Receipt
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const renderSearchFilters = () => (
    <Form className="mb-4">
      <Row>
        <Col md={3}>
          <Form.Group>
            <Form.Control 
              type="text" 
              placeholder="Receipt No" 
              value={searchParams.receiptNo}
              onChange={(e) => setSearchParams(prev => ({
                ...prev, 
                receiptNo: e.target.value
              }))}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Control 
              type="text" 
              placeholder="Client Name" 
              value={searchParams.clientName}
              onChange={(e) => setSearchParams(prev => ({
                ...prev, 
                clientName: e.target.value
              }))}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Select
            value={searchParams.transactionType}
            onChange={(e) => setSearchParams(prev => ({
              ...prev, 
              transactionType: e.target.value
            }))}
          >
            <option value="">All Types</option>
            {TRANSACTION_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control 
            type="date"
            value={searchParams.startDate}
            onChange={(e) => setSearchParams(prev => ({
              ...prev, 
              startDate: e.target.value
            }))}
          />
        </Col>
        <Col md={2}>
          <Form.Control 
            type="date"
            value={searchParams.endDate}
            onChange={(e) => setSearchParams(prev => ({
              ...prev, 
              endDate: e.target.value
            }))}
          />
        </Col>
      </Row>
    </Form>
  );

 const renderEditModal = () => (
  <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Edit Transaction</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {selectedTransaction && (
        <Form>
          {/* Transaction Basic Information */}
          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Receipt No</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.receiptNo || ''}
                disabled
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Client Name</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.clientName || ''}
                disabled
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Transaction Type</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.type || selectedTransaction.advanceType || 'N/A'}
                disabled
              />
            </Form.Group>
          </Row>

          {/* Transaction Details Table */}
          <div className="transaction-details-section">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Transaction Details</h5>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => {
                  const newTransaction = addNewTransactionRow(selectedTransaction);
                  setSelectedTransaction(newTransaction);
                }}
              >
                Add Row
              </Button>
            </div>

            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Sr.</th>
                  <th>Weight (gm)</th>
                  {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                    <th>Touch (%)</th>
                  )}
                  <th>Rate (₹)</th>
                  {selectedTransaction.articleType !== 'pureWeight' && <th>Pure Weight</th>}
                  <th>Amount (₹)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedTransaction.langdiWeight.map((weight, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={weight || 
                          (selectedTransaction.articleType === 'pureWeight' 
                            ? (selectedTransaction.pureWeightCalculated || 
                               selectedTransaction.weight || 
                               selectedTransaction.amount || 
                               '') 
                            : '')
                        }
                        onChange={(e) => {
                          const updatedTransaction = updateTransactionRow(
                            selectedTransaction, 
                            index, 
                            'weight', 
                            e.target.value
                          );
                          setSelectedTransaction(updatedTransaction);
                        }}
                      />
                    </td>
                    {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={selectedTransaction.touch[index] || ''}
                          onChange={(e) => {
                            const updatedTransaction = updateTransactionRow(
                              selectedTransaction, 
                              index, 
                              'touch', 
                              e.target.value
                            );
                            setSelectedTransaction(updatedTransaction);
                          }}
                        />
                      </td>
                    )}
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={selectedTransaction.rate[index] || ''}
                        onChange={(e) => {
                          const updatedTransaction = updateTransactionRow(
                            selectedTransaction, 
                            index, 
                            'rate', 
                            e.target.value
                          );
                          setSelectedTransaction(updatedTransaction);
                        }}
                      />
                    </td>
                    {selectedTransaction.articleType !== 'pureWeight' && (
                      <td>
                        {calculatePureWeight(
                          [weight], 
                          [selectedTransaction.touch[index]]
                        )}
                      </td>
                    )}
                    <td>
                      ₹{selectedTransaction.articleType === 'pureWeight'
                        ? (weight * (selectedTransaction.rate[index] || 0)).toLocaleString()
                        : calculateAmount(
                            weight, 
                            selectedTransaction.touch[index], 
                            selectedTransaction.rate[index]
                          ).toLocaleString()
                      }
                    </td>
                    <td>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          const updatedTransaction = removeTransactionRow(
                            selectedTransaction, 
                            index
                          );
                          setSelectedTransaction(updatedTransaction);
                        }}
                        disabled={selectedTransaction.langdiWeight.length <= 1}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
                
                {/* Total Row */}
                <tr className="table-footer">
                  <td colSpan={2}>Total</td>
                  <td>
                    {selectedTransaction.langdiWeight.reduce(
                      (sum, weight) => sum + (parseFloat(weight) || 0), 
                      0
                    ).toFixed(2)}
                  </td>
                  {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                    <td>-</td>
                  )}
                  <td>-</td>
                  {selectedTransaction.articleType !== 'pureWeight' && (
                    <td>
                      {calculatePureWeight(
                        selectedTransaction.langdiWeight, 
                        selectedTransaction.touch
                      )}
                    </td>
                  )}
                  <td>
                    ₹{selectedTransaction.totalAmounts.reduce(
                      (sum, amount) => sum + (parseFloat(amount) || 0), 
                      0
                    ).toLocaleString()}
                  </td>
                  <td>-</td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Financial Details */}
          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Total Amount</Form.Label>
              <Form.Control
                type="number"
                value={selectedTransaction.total || 0}
                disabled
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Amount Given</Form.Label>
              <Form.Control
                type="number"
                value={selectedTransaction.amountGiven || 0}
                onChange={(e) => {
                  const updatedTransaction = {
                    ...selectedTransaction,
                    amountGiven: parseFloat(e.target.value),
                    pendingAmount: parseFloat(e.target.value) - selectedTransaction.total
                  };
                  setSelectedTransaction(updatedTransaction);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Pending Amount</Form.Label>
              <Form.Control
                type="number"
                value={selectedTransaction.pendingAmount || 0}
                disabled
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Pure Weight Given</Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                value={selectedTransaction.pureWeightGiven || 0}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                  setSelectedTransaction(prev => ({
                    ...prev,
                    pureWeightGiven: value
                  }));
                }}
                placeholder="Enter Pure Weight Given"
              />
            </Form.Group>
          </Row>
        </Form>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={handleCloseModal}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={saveTransactionEdit}
        disabled={!selectedTransaction}
      >
        Save Changes
      </Button>
    </Modal.Footer>
  </Modal>
);

// Helper Functions for Transaction Editing
const addNewTransactionRow = (transaction) => {
  const lastIndex = transaction.langdiWeight.length - 1;
  const newTransaction = {
    ...transaction,
    langdiWeight: [
      ...transaction.langdiWeight, 
      transaction.langdiWeight[lastIndex] || 0
    ],
    touch: transaction.articleType === 'pureWeight' 
      ? [] 
      : [
          ...transaction.touch, 
          transaction.touch[lastIndex] || 0
        ],
    rate: [
      ...transaction.rate, 
      transaction.rate[lastIndex] || 0
    ],
    totalAmounts: [
      ...transaction.totalAmounts, 
      0
    ]
  };
  return newTransaction;
};

const removeTransactionRow = (transaction, indexToRemove) => {
  return {
    ...transaction,
    langdiWeight: transaction.langdiWeight.filter((_, index) => index !== indexToRemove),
    touch: transaction.touch.filter((_, index) => index !== indexToRemove),
    rate: transaction.rate.filter((_, index) => index !== indexToRemove),
    totalAmounts: transaction.totalAmounts.filter((_, index) => index !== indexToRemove)
  };
};

  return (
    <div className="transactions-container">
      {renderSearchFilters()}
      {renderTransactionTable()}
      {renderEditModal()}
    </div>
  );
};

export default Transactions;

// Utility function for status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'success';
    case 'pending': return 'warning';
    case 'cancelled': return 'danger';
    default: return 'secondary';
  }
};
